import { action, observable, makeObservable, flow, reaction } from 'mobx';
import { parseNumberByPrecision } from '../core/helpers/parseNumberByPrecision';
import { userService } from '../services/userService';
import { ChipValues } from '../core/constants/chipValues';
import { liveUpdateConnection } from '../services/liveConnection/LiveUpdateConnection';
import { DefaultQueryParams } from '../core/constants/defaultQueryParams';
import { parseQuery } from '../core/helpers/parseQuery';
import { iframeService } from '../services/iframeService';

class UserInfoStore {
  selectedChip = 10;

  betAmount = 0;

  totalBetAmount = 0;

  exchangedBetAmount = 0;

  userBalance = 0;

  limits = null;

  needToSyncCurrencies = false;

  precision = 2;

  defaultBetAmount = null;

  precisionForBetModel = 2;

  displayCurrency = null;

  displayCurrencyRate = 1;

  currencyFromToken = null;

  isAllowedChangeCurrency = true;

  needChangeCurrency = false;

  rate = 0;

  userId = '';

  currencyCode = '';

  currencyCodeAbbr = 'USD';

  margin = 3;

  rtp = 1 - this.margin / 100;

  quickBets = [];

  displayQuickBets = [];

  numpadBetAmountList = [];

  maxWinLimit = null;

  constructor() {
    makeObservable(this, {
      rtp: observable,
      rate: observable,
      userId: observable,
      margin: observable,
      quickBets: observable.deep,
      displayQuickBets: observable,
      displayCurrencyRate: observable,
      exchangedBetAmount: observable,
      numpadBetAmountList: observable,
      needToSyncCurrencies: observable,
      isAllowedChangeCurrency: observable,
      defaultBetAmount: observable,
      needChangeCurrency: observable,
      precision: observable,
      precisionForBetModel: observable,
      betAmount: observable,
      totalBetAmount: observable,
      userBalance: observable,
      maxWinLimit: observable,
      selectedChip: observable,
      displayCurrency: observable,
      currencyCode: observable,
      currencyCodeAbbr: observable,
      currencyFromToken: observable,
      limits: observable.deep,
      setHalve: action.bound,
      setRate: action.bound,
      setMargin: action.bound,
      setUserId: action.bound,
      setLimits: action.bound,
      setDouble: action.bound,
      setMaxWinLimit: action.bound,
      setQuickBets: action.bound,
      setDisplayQuickBets: action.bound,
      setPrecision: action.bound,
      setPrecisionForBetModel: action.bound,
      setBetAmount: action.bound,
      setTotalBetAmount: action.bound,
      setSelectedChip: action.bound,
      setDisplayCurrency: action.bound,
      setDisplayCurrencyRate: action.bound,
      setCurrencyCode: action.bound,
      setExchangedBetAmount: action.bound,
      setDefaultBetAmount: action.bound,
      setNeedChangeCurrency: action.bound,
      setCurrencyFromToken: action.bound,
      setIsAllowedChangeCurrency: action.bound,
      setUserBalance: action.bound,
      setUserConfigs: action.bound,
      setCurrencyCodeAbbr: action.bound,
      setNumpadBetAmountList: action.bound,
      sendExchangeAmount: action.bound,
      getBalance: flow,
    });
    reaction(
      () => this.currencyCodeAbbr + this.currencyFromToken,
      () => {
        this.needToSyncCurrencies = this.currencyCodeAbbr !== this.currencyFromToken;
      }
    );
    reaction(
      () => this.betAmount,
      () => {
        iframeService.sendStakeUpdate(this.betAmount);
      }
    );
  }

  setNumpadBetAmountList(quickBets) {
    this.numpadBetAmountList = quickBets.slice(0, 5);
  }

  setDefaultBetAmount(defaultAmount) {
    this.defaultBetAmount = defaultAmount;
  }

  setCurrencyCode(code) {
    this.currencyCode = code;
  }

  setMaxWinLimit(limit) {
    this.maxWinLimit = limit;
  }

  setCurrencyCodeAbbr(code) {
    this.currencyCodeAbbr = code;
  }

  setDisplayCurrency(currency) {
    this.displayCurrency = currency;
  }

  setDisplayCurrencyRate(rate) {
    this.displayCurrencyRate = rate;
  }

  setCurrencyFromToken(currency) {
    this.currencyFromToken = currency;
  }

  setIsAllowedChangeCurrency(value) {
    this.isAllowedChangeCurrency = value;
  }

  setNeedChangeCurrency(value) {
    this.needChangeCurrency = value;
  }

  setSelectedChip(value) {
    this.selectedChip = value;
  }

  setMargin(margin) {
    this.margin = margin;
    this.rtp = 1 - margin / 100;
  }

  setUserId(id) {
    this.userId = id;
  }

  setUserConfigs({ rate, limits, balance, precision, quickBets = [], displayCurrencyRate }) {
    this.setRate(rate);
    this.setUserBalance(balance);
    this.setPrecisionForBetModel(precision);
    this.setPrecision(precision);
    this.setDisplayCurrencyRate(displayCurrencyRate);
    this.setNumpadBetAmountList(quickBets.length ? quickBets : limits.quickBets);
  }

  setRate(rate) {
    this.rate = rate;
  }

  setPrecision(precision) {
    this.precision = precision || 3;
  }

  setPrecisionForBetModel(precision) {
    this.precisionForBetModel = precision;
  }

  setLimits(limits) {
    this.limits = limits;
    if (!this.betAmount && limits.defaultBetAmount) {
      this.betAmount = limits.defaultBetAmount;
      return;
    }
    if (!this.betAmount) {
      this.betAmount = limits.minBet;
    }
  }

  setUserBalance(balance) {
    this.userBalance = balance;
  }

  setBetAmount(betAmount) {
    this.betAmount = betAmount;
  }

  setTotalBetAmount(betAmount) {
    this.totalBetAmount = betAmount;
  }

  setExchangedBetAmount(exchangedBetAmount) {
    this.exchangedBetAmount = exchangedBetAmount;
  }

  setDouble() {
    if (this.betAmount === this.limits.maxBet) return;
    if (this.betAmount * 2 > this.limits.maxBet) {
      this.betAmount = this.limits.maxBet;
    }
    if (this.betAmount * 2 <= this.limits.maxBet) {
      this.betAmount = parseNumberByPrecision(this.betAmount, 2, this.precision);
    }
  }

  setHalve() {
    if (this.betAmount === this.limits.minBet) return;
    if (this.betAmount / 2 < this.limits.minBet) {
      this.betAmount = this.limits.minBet;
    }
    if (this.betAmount / 2 >= this.limits.minBet) {
      this.betAmount = parseNumberByPrecision(this.betAmount, 0.5, this.precision);
    }
  }

  setQuickBets(quickBets, chipsColors, displayQuickBets = []) {
    this.quickBets = ChipValues.map((chip, index) => ({
      chip,
      displayValue: displayQuickBets?.[index],
      value: quickBets[index],
      color: chipsColors[index],
    }));
  }

  setDisplayQuickBets(quickBets) {
    this.displayQuickBets = quickBets;
  }

  *getBalance() {
    const {
      data: { result },
    } = yield userService.getBalance();
    this.setUserBalance(result);
  }

  sendExchangeAmount(debouncedValue, isInTotal = true) {
    const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
    if (
      gameId &&
      this.userId &&
      ((!this.displayCurrency && this.limits && this.rate) ||
        (this.displayCurrency && this.displayQuickBets?.length && this.displayCurrencyRate))
    ) {
      liveUpdateConnection.sendExchangeAmount({
        gameId,
        userId: this.userId,
        amount: debouncedValue,
        exchangeType: isInTotal && !!this.displayCurrency,
        precision: this.precision,
        rate: this.displayCurrency ? this.displayCurrencyRate : this.rate,
        limits:
          isInTotal && this.displayCurrency
            ? this.displayQuickBets
            : [this.limits.minBet, this.limits.maxBet],
      });
    }
  }
}

export const userInfoStore = new UserInfoStore();
